import { BaseFilter } from './BaseFilter.js'

/**
 * Base class for room-specific filters
 */
export class RoomFilter extends BaseFilter {
	constructor(id, label, active = false) {
		super(id, label, active)
		this.type = 'room'
	}

	/**
	 * Apply filter to a room resource
	 * @param {Object} _room - Room resource object
	 * @param {Object} _context - Additional context (unused in base class)
	 * @returns {boolean}
	 */
	apply(_room, _context = {}) {
		throw new Error('apply() method must be implemented by room filter subclasses')
	}
}

/**
 * Filter to show only bookable rooms
 */
export class BookableRoomsFilter extends RoomFilter {
	constructor(active = false) {
		super('bookable-rooms', 'Nur buchbare Räume', active)
	}

	apply(room, _context = {}) {
		if (!this.active) return true
		return room.permissions?.canCreateRegular === true
	}
}

/**
 * Filter rooms by building
 */
export class BuildingFilter extends RoomFilter {
	constructor(buildingId, buildingName, active = false) {
		super(`building-${buildingId}`, buildingName, active)
		this.buildingId = buildingId
	}

	apply(room, _context = {}) {
		if (!this.active) return true
		return room.building === this.buildingId || room.buildingName === this.label
	}

	serialize() {
		return {
			...super.serialize(),
			buildingId: this.buildingId
		}
	}

	deserialize(state) {
		super.deserialize(state)
		this.buildingId = state.buildingId
	}
}


/**
 * Filter rooms by multiple equipment items (multi-select)
 */
export class MultiEquipmentFilter extends RoomFilter {
	constructor(active = false, selectedEquipmentIds = []) {
		super('multi-equipment', 'Ausstattung', active)
		this.selectedEquipmentIds = new Set(selectedEquipmentIds.map(id => parseInt(id)))
		this.equipmentOptions = new Map() // id -> name mapping
	}

	/**
	 * Set available equipment options
	 * @param {Array} equipmentList - Array of {id, name} objects
	 */
	setEquipmentOptions(equipmentList) {
		this.equipmentOptions.clear()
		equipmentList.forEach(eq => {
			this.equipmentOptions.set(parseInt(eq.id), eq.name)
		})
	}

	/**
	 * Add equipment to selection
	 * @param {number} equipmentId 
	 */
	addEquipment(equipmentId) {
		this.selectedEquipmentIds.add(parseInt(equipmentId))
		this._updateActiveState()
	}

	/**
	 * Remove equipment from selection
	 * @param {number} equipmentId 
	 */
	removeEquipment(equipmentId) {
		this.selectedEquipmentIds.delete(parseInt(equipmentId))
		this._updateActiveState()
	}

	/**
	 * Clear all equipment selections
	 */
	clearSelection() {
		this.selectedEquipmentIds.clear()
		this.setActive(false)
	}

	/**
	 * Get selected equipment IDs as array
	 */
	getSelectedEquipmentIds() {
		return Array.from(this.selectedEquipmentIds)
	}

	/**
	 * Get selected equipment names
	 */
	getSelectedEquipmentNames() {
		return this.getSelectedEquipmentIds()
			.map(id => this.equipmentOptions.get(id))
			.filter(name => name)
	}

	/**
	 * Update active state based on selection
	 * @private
	 */
	_updateActiveState() {
		this.active = this.selectedEquipmentIds.size > 0
	}

	/**
	 * Get display label for the filter
	 */
	getDisplayLabel() {
		const names = this.getSelectedEquipmentNames()
		if (names.length === 0) return this.label
		if (names.length === 1) return names[0]
		if (names.length <= 3) return names.join(', ')
		return `${names.length} Ausstattungen`
	}

	apply(room, _context = {}) {
		if (!this.active || this.selectedEquipmentIds.size === 0) return true
		
		// Room must have ALL selected equipment items
		if (Array.isArray(room.equipments)) {
			const roomEquipmentIds = room.equipments.map(id => parseInt(id))
			return Array.from(this.selectedEquipmentIds).every(reqId => 
				roomEquipmentIds.includes(reqId)
			)
		}
		
		return false
	}

	getConfig() {
		return {
			...super.getConfig(),
			selectedEquipmentIds: this.getSelectedEquipmentIds(),
			displayLabel: this.getDisplayLabel()
		}
	}

	serialize() {
		return {
			...super.serialize(),
			selectedEquipmentIds: this.getSelectedEquipmentIds()
		}
	}

	deserialize(state) {
		super.deserialize(state)
		this.selectedEquipmentIds = new Set(
			(state.selectedEquipmentIds || []).map(id => parseInt(id))
		)
		this._updateActiveState()
	}
}

/**
 * Filter rooms by multiple room types (multi-select)
 */
export class MultiRoomTypeFilter extends RoomFilter {
	constructor(active = false, selectedRoomTypeIds = []) {
		super('multi-room-types', 'Raumtyp', active)
		this.selectedRoomTypeIds = new Set(selectedRoomTypeIds.map(id => parseInt(id)))
		this.roomTypeOptions = new Map() // id -> name mapping
	}

	/**
	 * Set available room type options
	 * @param {Array} roomTypeList - Array of {id, name} objects
	 */
	setRoomTypeOptions(roomTypeList) {
		this.roomTypeOptions.clear()
		roomTypeList.forEach(type => {
			this.roomTypeOptions.set(parseInt(type.id), type.name)
		})
	}

	/**
	 * Add room type to selection
	 * @param {number} roomTypeId 
	 */
	addRoomType(roomTypeId) {
		this.selectedRoomTypeIds.add(parseInt(roomTypeId))
		this._updateActiveState()
	}

	/**
	 * Remove room type from selection
	 * @param {number} roomTypeId 
	 */
	removeRoomType(roomTypeId) {
		this.selectedRoomTypeIds.delete(parseInt(roomTypeId))
		this._updateActiveState()
	}

	/**
	 * Clear all room type selections
	 */
	clearSelection() {
		this.selectedRoomTypeIds.clear()
		this.setActive(false)
	}

	/**
	 * Get selected room type IDs as array
	 */
	getSelectedRoomTypeIds() {
		return Array.from(this.selectedRoomTypeIds)
	}

	/**
	 * Get selected room type names
	 */
	getSelectedRoomTypeNames() {
		return this.getSelectedRoomTypeIds()
			.map(id => this.roomTypeOptions.get(id))
			.filter(name => name)
	}

	/**
	 * Update active state based on selection
	 * @private
	 */
	_updateActiveState() {
		this.active = this.selectedRoomTypeIds.size > 0
	}

	/**
	 * Get display label for the filter
	 */
	getDisplayLabel() {
		const names = this.getSelectedRoomTypeNames()
		if (names.length === 0) return this.label
		if (names.length === 1) return names[0]
		if (names.length <= 3) return names.join(', ')
		return `${names.length} Raumtypen`
	}

	apply(room, _context = {}) {
		if (!this.active || this.selectedRoomTypeIds.size === 0) return true
		
		// Room must have ANY of the selected room types
		if (Array.isArray(room.roomTypes)) {
			const roomTypeIds = room.roomTypes.map(id => parseInt(id))
			return Array.from(this.selectedRoomTypeIds).every(reqId => 
				roomTypeIds.includes(reqId)
			)
		}
		
		return false
	}

	getConfig() {
		return {
			...super.getConfig(),
			selectedRoomTypeIds: this.getSelectedRoomTypeIds(),
			displayLabel: this.getDisplayLabel()
		}
	}

	serialize() {
		return {
			...super.serialize(),
			selectedRoomTypeIds: this.getSelectedRoomTypeIds()
		}
	}

	deserialize(state) {
		super.deserialize(state)
		this.selectedRoomTypeIds = new Set(
			(state.selectedRoomTypeIds || []).map(id => parseInt(id))
		)
		this._updateActiveState()
	}
}

/**
 * Filter rooms by minimum seat count
 */
export class SeatCountFilter extends RoomFilter {
	constructor(active = false, minSeats) {
		super('seat-count', 'Sitzplätze', active)
		this.minSeats = minSeats ? parseInt(minSeats) : undefined
	}

	/**
	 * Set minimum seat count
	 * @param {number} minSeats 
	 */
	setMinSeats(minSeats) {
		this.minSeats = minSeats ? parseInt(minSeats) : undefined
		this._updateActiveState()
	}

	/**
	 * Get minimum seat count
	 */
	getMinSeats() {
		return this.minSeats
	}

	/**
	 * Clear seat count selection
	 */
	clearSelection() {
		this.minSeats = undefined
		this.setActive(false)
	}

	/**
	 * Update active state based on seat count
	 * @private
	 */
	_updateActiveState() {
		this.active = this.minSeats !== undefined && this.minSeats > 0
	}

	/**
	 * Get display label for the filter
	 */
	getDisplayLabel() {
		if (!this.active) return this.label
		return `≥ ${this.minSeats} Plätze`
	}

	apply(room, _context = {}) {
		if (!this.active) return true
		
		const roomSeats = parseInt(room.seats) || 0
		return roomSeats >= this.minSeats
	}

	getConfig() {
		return {
			...super.getConfig(),
			minSeats: this.minSeats,
			displayLabel: this.getDisplayLabel()
		}
	}

	serialize() {
		return {
			...super.serialize(),
			minSeats: this.minSeats
		}
	}

	deserialize(state) {
		super.deserialize(state)
		this.minSeats = state.minSeats ? parseInt(state.minSeats) : undefined
		this._updateActiveState()
	}
}

/**
 * Filter rooms by search query (searches in room title/door label/name)
 */
export class RoomSearchFilter extends RoomFilter {
	constructor(active = false, searchQuery = '') {
		super('room-search', 'Raum suchen', active)
		this.searchQuery = searchQuery || ''
	}

	/**
	 * Set search query
	 * @param {string} query
	 */
	setSearchQuery(query) {
		this.searchQuery = (query || '').trim()
		this._updateActiveState()
	}

	/**
	 * Get current search query
	 */
	getSearchQuery() {
		return this.searchQuery
	}

	/**
	 * Clear search query
	 */
	clearSearch() {
		this.searchQuery = ''
		this.setActive(false)
	}

	/**
	 * Update active state based on search query
	 * @private
	 */
	_updateActiveState() {
		this.active = this.searchQuery.length > 0
	}

	/**
	 * Get display label for the filter
	 */
	getDisplayLabel() {
		if (!this.active || !this.searchQuery) return this.label
		return `Suche: "${this.searchQuery}"`
	}

	apply(room, _context = {}) {
		if (!this.active || !this.searchQuery) return true

		// Search in room title (which contains doorLabel and name)
		const searchLower = this.searchQuery.toLowerCase()
		const title = (room.title || '').toLowerCase()
		const description = (room.description || '').toLowerCase()

		return title.includes(searchLower) || description.includes(searchLower)
	}

	getConfig() {
		return {
			...super.getConfig(),
			searchQuery: this.searchQuery,
			displayLabel: this.getDisplayLabel()
		}
	}

	serialize() {
		return {
			...super.serialize(),
			searchQuery: this.searchQuery
		}
	}

	deserialize(state) {
		super.deserialize(state)
		this.searchQuery = state.searchQuery || ''
		this._updateActiveState()
	}
}
